import { FUNNEL_EVENTS, emitFunnelEvent } from "@/lib/funnel-analytics";
import { hasSeenLimitWarning } from "@/components/billing/usage-meter";
import type { LimitPromptOutcome } from "@/components/billing/upgrade-prompt";

/**
 * ONB-023 — emit `limit_reached` with the prompt's outcome.
 *
 * This is the one ticket in P2 that no single workstream owned: the event is
 * registered by ONB-010, the outcome comes from ONB-021's prompt, and whether
 * a warning preceded the block is only known to ONB-022's meter. Joining them
 * anywhere else would have meant one of those three reaching into another.
 *
 * ── Why `warning_seen_before_block` is here ───────────────────────────────
 * ONB-022's acceptance criterion is "the blocking prompt is never a user's
 * first news of a limit". That is a sentence about sequencing, and until now
 * it could only be reviewed, never counted. `prompt_shown: true` together with
 * `warning_seen_before_block: false` IS that failure — so the criterion
 * becomes a number someone can watch in production rather than an intention
 * someone has to remember.
 *
 * The plan's own justification for the whole event: "Which limits actually
 * drive upgrades — and which just annoy people." A limit that fires often and
 * is always dismissed is annoying people; one that converts is doing its job.
 * That distinction needs the OUTCOME, which is why this waits for the prompt
 * to resolve rather than firing when it opens.
 */
export function trackLimitReached(outcome: LimitPromptOutcome): void {
  const limitKey = outcome.limitKey ?? outcome.resource ?? "unknown";

  emitFunnelEvent(FUNNEL_EVENTS.LIMIT_REACHED, {
    resource: outcome.resource ?? limitKey,
    // The catalog declares `limit` required and numeric. A server that sent no
    // ceiling gives `-1` — the same "unlimited/unknown" sentinel `PlanLimit`
    // already uses — rather than `0`, which would read as "you may have none".
    limit: outcome.limit ?? -1,
    plan_slug: outcome.targetPlanSlug ?? "unknown",
    prompt_shown: outcome.promptShown,
    // Both declared OPTIONAL on the catalog row, which is what lets them be
    // added to an event that was already live without making every previously
    // emitted `limit_reached` a schema-gate violation.
    //
    // `warning_seen_before_block` turns ONB-022's acceptance criterion — "the
    // blocking prompt is never a user's first news of a limit" — from a
    // sentence that can only be reviewed into a number that can be watched:
    // `prompt_shown: true` with `warning_seen_before_block: false` IS that
    // failure, in production, counted.
    //
    // `intro_offer_shown` is ONB-052's denominator. The offer can only be
    // judged against a holdout if the moment it was NOT shown is recorded,
    // and this is the event where the arm is decided — everyone who reaches
    // `checkout_started` is in the numerator by definition.
    warning_seen_before_block: hasSeenLimitWarning(limitKey),
    intro_offer_shown: outcome.introOfferShown,
  });
}

/**
 * The richer picture, for callers that want the whole outcome in one object.
 *
 * `warning_seen_before_block` and `intro_offer_shown` are now DECLARED on the
 * catalog row and emitted by {@link trackLimitReached} directly, so this is no
 * longer the only place they exist. What stays exclusive to this function is
 * `action` and `alternative_offered` — deliberately undeclared, because
 * `alternative_offered` names an in-product resource and the catalog's privacy
 * gate is the right place for that judgement, not a property added in passing.
 */
export function limitReachedContext(outcome: LimitPromptOutcome): {
  warning_seen_before_block: boolean;
  action: LimitPromptOutcome["action"];
  alternative_offered: string;
  intro_offer_shown: boolean;
} {
  const limitKey = outcome.limitKey ?? outcome.resource ?? "unknown";
  return {
    warning_seen_before_block: hasSeenLimitWarning(limitKey),
    action: outcome.action,
    alternative_offered: outcome.alternativeOffered ?? "none",
    intro_offer_shown: outcome.introOfferShown,
  };
}

// ── ONB-052 — making the discount measurable ───────────────────────────────
//
// The question this has to answer, and the only reason the intro offer is
// allowed to exist: does it convert people who would otherwise have stayed on
// Free, or does it just defer revenue from people who were going to pay full
// price anyway? Answering it needs the funnel
// `limit_reached → checkout_started` split by WHETHER THE OFFER WAS SHOWN,
// not by which button was pressed — that is what makes a holdout comparable.
//
// Deliberately NOT an experiment framework. There is no assignment, no
// bucketing and no cohort store: the arm is simply an observation of what the
// prompt rendered, carried on the URL to the checkout surface that emits
// `checkout_started`, because that surface is a full page navigation away and
// nothing else survives it.

/**
 * The query-string key the limit prompt writes and a checkout surface reads.
 *
 * Short and non-PII on purpose — it ends up in a URL a user can see, share
 * and edit. A hand-edited value is not a problem: it can only mislabel that
 * one person's `checkout_started`, and {@link parseIntroOfferArm} rejects
 * anything it does not recognise.
 */
export const INTRO_OFFER_QUERY_KEY = "offer";

/**
 * Which arm one checkout came from.
 *
 * - `none`  — no intro offer was rendered. This is the holdout, and it is
 *             also the default state of every plan today (no coupon → no
 *             offer), so it is what the property reads with the feature off.
 * - `shown` — the offer was on screen and the user chose the full price
 *             anyway. This is the arm that measures deferred revenue.
 * - `taken` — the offer was on screen and the user took it.
 */
export type IntroOfferArm = "none" | "shown" | "taken";

/** Narrows an untrusted query value. Anything unrecognised is `none`. */
export function parseIntroOfferArm(value: string | null | undefined): IntroOfferArm {
  return value === "shown" || value === "taken" ? value : "none";
}

/**
 * `checkout_started`'s `source`, with the arm folded in.
 *
 * `source` is already a declared, optional string on the event
 * (`packages/types/src/product-analytics.ts`), and "which surface, under
 * which offer" genuinely is one source — so this needs no catalog change and
 * cannot land as `unregistered`. `none` returns the base string untouched, so
 * every `checkout_started` emitted before this ticket, and every one emitted
 * with the offer switched off, stays byte-identical and historical data stays
 * comparable.
 *
 * The DENOMINATOR of this funnel lives on `limit_reached`, which now
 * declares `intro_offer_shown` as an optional property for exactly that
 * purpose — so both halves of the comparison are emitted and the offer can be
 * judged against a real holdout. Overloading `resource` or `plan_slug` to
 * carry the arm would have corrupted two properties to avoid adding one.
 */
export function checkoutSourceForArm(base: string, arm: IntroOfferArm): string {
  return arm === "none" ? base : `${base}:offer_${arm}`;
}
